import {
  customer,
  Debtor,
  loan_application,
  person,
} from '@axo/shared/data-access/types';
import { MarketCountryCode } from '@axo/shared/types';
import { isNil, map, omitBy, pick, reduce } from 'lodash';
import { PrefixedData, ReloadedFormData } from './types/dataTypes';

const prefixAttributes = <P extends string, O extends object>(
  object: O,
  prefix: P
): { [T in keyof O as `${P & string}${T & string}`]: O[T] } =>
  reduce(
    object,
    (acc, value, key) => {
      const prefixedKey = `${prefix}${key}` as `${P}${typeof key}`;
      return { ...acc, [prefixedKey]: value };
    },
    {} as {
      [K in keyof O as `${P & string}${K & string}`]: O[K];
    }
  );

const hasWithdrawnMarketingConsent = (
  marketingConsent?: customer.Customer['MarketingConsent']
) => !!marketingConsent?.WithdrawnAt;

const hasMarketingConsent = (
  MarketingConsent?: customer.Customer['MarketingConsent']
) =>
  !!MarketingConsent &&
  !!MarketingConsent.ConsentText &&
  !MarketingConsent.WithdrawnAt;

const convertToDebtChanges = (debts: Partial<loan_application.Debt>[]) => {
  const transformedDebts = debts.map((debt) => {
    const prefix = debt.Debtor === Debtor.CoApplicant ? 'CoDebt' : 'Debt';
    return prefixAttributes(debt, `${prefix}/${debt.Type}/`);
  });
  return reduce(transformedDebts, (acc, curr) => ({ ...acc, ...curr }));
};

const parseLeads = (leads: loan_application.Lead[] | undefined) => {
  const result = {};

  if (!leads?.length) return result;

  for (const lead of leads) {
    Object.assign(result, {
      [`Lead/${[loan_application.Partner[lead.Partner]]}/Consent`]: true,
    });
  }

  return result;
};

const parseExpenseChanges = (
  expenses: loan_application.Expense[] | undefined
) => {
  const result = {};

  if (!expenses?.length) return result;

  for (const expense of expenses) {
    Object.assign(result, {
      [`Expense/${[loan_application.ExpenseKind[expense.Kind]]}/Value`]:
        expense.Value,
    });
  }

  return result;
};

const parseFrontendChanges = (data: object[] | undefined, prefix: string) => {
  const result = {};

  if (!data?.length) return result;

  for (const i in data) {
    const item = data[i];
    Object.assign(result, prefixAttributes(item, `${prefix}/${i}/`));
  }

  return result;
};

const parseFrontendDebts = (data: loan_application.Debt[] | undefined) => {
  if (!data || !data?.length) return;

  const result = data.map((debt) => {
    if (!debt.RefinanceAmount) return debt;

    return {
      ...debt,
      IsPartOfRefinancing: debt.RefinanceAmount > 0,
    };
  });

  return result;
};

/**
 * A function to pick out only the fields that are relevant for the frontend. It
 * also allows us to explicitly say which fields we expect to remove from the
 * data from the backend, so that if we ignore some data from the backend that has not
 * been explicitly omitted, an error will be logged to the console.
 *
 * Originally we wanted to be explicit about which fields the frontend uses, so
 * that the form state is not polluted by backend fields, but this makes
 * possible that we ignore something that should not be ignored. This function
 * attempts to make it easier to catch and debug issues related to the
 * omitting of data from the backend when reloading frontend state.
 */
const strictPick = <T extends object, U extends keyof T>(
  obj: T | undefined,
  pickFields: U[],
  omitFields: U[]
) => {
  if (!obj) return {};
  const unknownFields = Object.keys(obj).filter(
    (key) => !pickFields.includes(key as U) && !omitFields.includes(key as U)
  );

  if (unknownFields.length > 0) {
    console.log(
      'Omitted unknown fields when reloading from backend:',
      unknownFields
    );
  }

  return pick(obj, pickFields);
};

type StripUnusedDataArgs = {
  loanApplication: loan_application.LoanApplication;
  customer: customer.Customer;
  debts: loan_application.Debt[];
  cars?: loan_application.Car[];
  person?: person.Person;
  coPerson?: person.Person;
};
export const stripUnusedData = ({
  loanApplication,
  customer,
  debts,
  cars,
  coPerson,
  person,
}: StripUnusedDataArgs) => {
  return {
    loanApplication: strictPick(
      loanApplication,
      [
        // Loan
        'AppliedAmount',
        'LoanDuration',
        'InitialPurpose',
        'OtherPurpose',
        'Purpose',
        'DataFetchedFrom',

        // Employment
        'EmploymentStatus',
        'EmploymentStatusSince',
        'EmploymentStatusUntil',
        'Employer',
        'EmployerIndustry',
        'JobTitle',
        'JobPosition',
        'CompanyOrganizationNumber',
        'GrossIncomeYearly',
        'NetIncomeMonthly',
        'ExtraIncomeMonthly',
        'Education',

        // Housing
        'HousingType',
        'OwnsHolidayHome',
        'ApartmentType',
        'RentCostMonthly',
        'OwnerAssociationCostMonthly',
        'ExtraIncomeRental',
        'LivingCostMonthly',

        // Partner
        'MaritalStatus',
        'PartnerGrossIncomeYearly',
        'PartnerNetIncomeYearly',
        'PartnerLivingCostMonthly',
        'ExtraIncomeChildSupport',

        'TotalRefinancingAmount',

        // Citizenship
        'Citizenship',
        'ResidencePermit',
        'ResidencePermitType',
        'ResidencePermitNumber',
        'ResidencePermitExpiration',
        'LivedInMarketCountrySince',
        'PoliticallyInfluential',
        'MilitaryServiceCompleted',

        // Bank
        'AccountClearingNumber',
        'AccountNumber',
        'MainBank',
        'HasDankort',
        'HasOtherPaymentCards',
        'YearsAsBankCustomer',
        'RegistrationNumber',
        'PaymentRemarks',
        'HasUnemploymentFund',
        'CurrentYearDebtOutstandingAmount',

        'HasCars',
        'Expenses',
        'CreditStatusConsent',

        // CoApplicant
        'CoApplicantIsCohabitant',
        'CoApplicantMaritalStatus',
        'CoApplicantEmploymentStatus',
        'CoApplicantGrossIncomeYearly',
        'CoApplicantNetIncomeYearly',
        'CoApplicantMonthlyExpenses',
        'CoApplicantEmployer',
        'CoApplicantEmploymentStatusSince',
        'CoApplicantEmploymentStatusUntil',
        'CoApplicantHousingType',
        'CoApplicantRentCostMonthly',
        'CoApplicantResidencePermitType',
        'CoApplicantResidencePermitNumber',
        'CoApplicantResidencePermitExpiration',
        'CoApplicantHasUnemploymentFund',
        'CoApplicantCompanyOrganizationNumber',
      ],
      [
        'Debts',
        'Cars',
        'ID',
        'CreatedAt',
        'UpdatedAt',
        'CustomerID',
        'PersonID',
        'IP',
        'Status',
        'Currency',
        'MarketCountry',
        'HumanReadableID',
        'EmailReminders',
        'SMSReminders',
        'Product',
        'WaitingForBanks',
        'Children',
        'PartnerGrossIncomeMonthly',
        'GrossIncomeMonthly',
      ]
    ),
    customer: strictPick(
      customer,
      ['Email', 'MobilePhoneNumber'],
      [
        'ID',
        'Revision',
        'CreatedAt',
        'UpdatedAt',
        'MobilePhoneCountryCode',
        'MarketCountry',
      ]
    ),
    debts: map(debts, (debt) =>
      strictPick(
        debt,
        ['RefinanceAmount', 'MonthlyDebtPayment', 'OutstandingAmount'],
        [
          'ID',
          'LoanApplicationID',
          'Type',
          'Creditor',
          'Debtor',
          'NominalInterestRate',
        ]
      )
    ),
    cars: map(cars, (car) =>
      strictPick(
        car,
        ['TotalCostMonthly', 'OwnershipType'],
        ['ID', 'LoanApplicationID']
      )
    ),
    person: strictPick(
      person,
      ['SocialSecurityNumber', 'LivedAtAddressSince'],
      [
        'ID',
        'Revision',
        'CreatedAt',
        'UpdatedAt',
        'MarketCountry',
        'Sex',
        'Age',
        'HasCompleteAddress',
        'HasCompleteName',
      ]
    ),
    coPerson: strictPick(
      coPerson,
      ['SocialSecurityNumber'],
      [
        'ID',
        'Revision',
        'CreatedAt',
        'UpdatedAt',
        'MarketCountry',
        'LivedAtAddressSince',
        'Sex',
        'Age',
        'HasCompleteAddress',
        'HasCompleteName',
      ]
    ),
  };
};

type ConvertToFormDataArgs = {
  marketCountry: MarketCountryCode;
  loanApplication: loan_application.LoanApplication;
  customer: customer.Customer;
  consent?: boolean;
  debts: loan_application.Debt[];
  children: loan_application.Child[];
  cars?: loan_application.Car[];
  person?: person.Person;
  coPerson?: person.Person;
  expenses?: loan_application.Expense[];
  leads?: loan_application.Lead[];
};
export const convertToFormData = ({
  marketCountry,
  children,
  customer,
  consent,
  debts,
  loanApplication,
  cars,
  coPerson,
  person,
  expenses,
  leads,
}: ConvertToFormDataArgs): ReloadedFormData => {
  const strippedData = stripUnusedData({
    loanApplication,
    customer,
    debts,
    cars,
    person,
    coPerson,
  });

  // if the user has not consented already to marketing, or has withdrawn consent, we want to tick the checkbox to true as default.
  const wantUserToConsentToMarketing =
    !hasMarketingConsent(customer.MarketingConsent) ||
    hasWithdrawnMarketingConsent(customer.MarketingConsent);

  // set this to the state of the marketingConsent that is the actual state of the users consent. Which will be the state of the button on reload.
  const userHasMarketingConsent = hasMarketingConsent(
    customer.MarketingConsent
  );

  const transformedLeads = parseLeads(leads);

  const prefixedData: Partial<PrefixedData> = {
    // Shared Markets Attributes
    ...prefixAttributes(strippedData.loanApplication, 'LoanApplication/'),
    ...prefixAttributes(strippedData.customer, 'Customer/'),
    ...prefixAttributes(
      {
        MarketingConsent: consent ?? userHasMarketingConsent,
        ShowMarketingConsent: !consent || wantUserToConsentToMarketing,
      },
      'Ignore/'
    ),
    ...(person ? prefixAttributes(strippedData.person, 'Person/') : {}),
    ...(coPerson ? prefixAttributes(strippedData.coPerson, 'CoPerson/') : {}),
    AmountOfChildren: children.length.toString(),
    ...transformedLeads,
  };

  // Specific Markets Attributes
  switch (marketCountry) {
    case MarketCountryCode.Denmark:
      {
        const transformedCars = parseFrontendChanges(cars, 'Car');
        const transformedChildren = parseFrontendChanges(children, 'Child');
        const transformedExpenses = parseExpenseChanges(expenses);
        const transformedDebts = parseFrontendDebts(debts);

        // Set Debt[0] as the DebtCollection Debt
        transformedDebts?.sort((debt) => {
          if (debt?.Type === loan_application.DebtType.Collection) {
            return -1;
          }
          return 1;
        });

        Object.assign(prefixedData, {
          ...transformedCars,
          ...transformedChildren,
          ...transformedExpenses,
        });

        if (transformedDebts) {
          Object.assign(prefixedData, {
            'LoanApplication/Debts': transformedDebts,
          });
        }
      }
      break;

    default:
      {
        const mappedDebts = convertToDebtChanges(debts);

        Object.assign(prefixedData, {
          ...mappedDebts,
        });
      }
      break;
  }

  const sanitizedData = omitBy(prefixedData, isNil) satisfies ReloadedFormData;

  return sanitizedData;
};
